/* Tagalys Headless usage documentation:
 ** - https://tagalys.notion.site/Search-results-666b0999526a4ac490de4c2664bfd165#43e375c82e5b46e7bce3573067a1aabc
 */

/* Recommended extension
 ** - https://marketplace.visualstudio.com/items?itemName=Tobermory.es6-string-html
 */

const { debounce, initYotpoWidgets, translateString } = window.BAO.Utils;
const { ProductCard } = window.BAO.Tagalys;

class SuggestedResources {
  static get resourceTypes() {
    return ["collections", "pages"];
  }

  async fetchResources() {
    const storedResources = window.sessionStorage.getItem(this.storageKey);

    if (storedResources?.length) {
      this.resources = JSON.parse(storedResources);
      return;
    }

    const response = await Promise.all(
      this.constructor.resourceTypes.map((resource) => {
        return fetch(`/?view=${resource}`).then((response) => response.json());
      }),
    );

    this.resources = this.constructor.resourceTypes.reduce(
      (resources, resourceType, index) => {
        return { ...resources, [resourceType]: response[index] || [] };
      },
      {},
    );
  }

  filterResources(resourceType, term) {
    if (!this.resources?.[resourceType] || !term?.length) return [];

    return this.resources[resourceType]
      .map((resource) => {
        const includesTerm = resource.title
          ?.toLowerCase()
          ?.includes(term?.toLowerCase());
        return includesTerm ? resource : null;
      })
      .filter(Boolean);
  }

  set resources(resources) {
    this._resources = resources;
    window.sessionStorage.setItem(this.storageKey, JSON.stringify(resources));
  }

  get resources() {
    return this._resources || [];
  }

  get storageKey() {
    return "suggested-resources";
  }
}

class TagalysQuickSearch extends window.BAO.CustomElement() {
  static get requiredElements() {
    return [
      "input-mobile",
      "results",
      "suggested-collections",
      "suggested-pages",
      "recent-searches",
      "popular-searches",
    ];
  }

  static get requiredExternalElements() {
    return ["trigger", "input-desktop"];
  }

  async connectedCallback() {
    super.connectedCallback();

    this.setAttribute("state", "default");

    this.suggestedResources = new SuggestedResources();
    await this.suggestedResources.fetchResources();
  }

  setupListeners() {
    super.setupListeners();

    if (!this.inputDesktopEl || !this.inputMobileEl) return;

    this.externalEls?.trigger?.elements?.forEach((triggerEl) => {
      this.listeners.add(triggerEl, "click", () => {
        if (window.matchMedia("(min-width: 901px)")?.matches) {
          this.inputDesktopEl?.focus();
        } else {
          this.inputMobileEl?.focus();
        }
      });
    });

    this.debouncedHandleInput = debounce(this.handleInput.bind(this), 250);
    this.debouncedHandleInput = this.debouncedHandleInput.bind(this);

    this.listeners.add(this.inputMobileEl, "input", this.debouncedHandleInput);
    this.listeners.add(this.inputDesktopEl, "input", this.debouncedHandleInput);

    if (!window?.onTagalysReady) return;

    // eslint-disable-next-line no-undef
    onTagalysReady(this.initialise.bind(this));
  }

  async initialise() {
    // eslint-disable-next-line no-undef
    this.searchSuggestions = Tagalys.APIConnector.SearchSuggestions.new({
      params: {
        request: {
          brands: { limit: 0 },
          products: { limit: this.maximumResultCount + 1 },
        },
      },
      onSuccess: this.handleSuccess.bind(this),
      onFailure(response, helpers) {
        // TODO: handle failure
        this.log(response);
      },
    });

    await this.renderRecentAndPopularSearches();
  }

  handleSuccess(response, helpers) {
    this.response = response;
    this.helpers = helpers;

    if (response?.products?.length) {
      this.recordRecentSearch();
      this.renderResults();
    } else {
      this.renderNoResults();
    }

    this.setAttribute("state", "results");

    initYotpoWidgets();
  }

  async renderResults() {
    this.resultsEl.innerHTML = await this.productListHTML();
    window.yotpo?.refreshWidgets();
    this.updateResultsTitle(this.response?.products?.length);
    this.updateAllResultsLink(true);
  }

  renderNoResults() {
    this.resultsEl.innerHTML = this.noResultsHTML();
    this.updateResultsTitle("");
    this.updateAllResultsLink(false);
  }

  renderSuggestedResources() {
    const resourceLimit = 5;

    const collections = this.suggestedResources
      .filterResources("collections", this.searchTerm)
      ?.slice(0, resourceLimit);
    const pages = this.suggestedResources
      .filterResources("pages", this.searchTerm)
      ?.slice(0, resourceLimit);

    this.renderSuggestedResource(this.suggestedCollectionsEl, collections);
    this.renderSuggestedResource(this.suggestedPagesEl, pages);
  }

  renderSuggestedResource(element, resource) {
    element
      ?.closest("[aria-hidden]")
      ?.setAttribute("aria-hidden", !resource?.length);
    if (!resource?.length || !element) return;
    element.innerHTML = this.suggestedItemsHTML(resource);
  }

  async renderRecentAndPopularSearches() {
    const recentAndPopularSearches =
      await this.searchSuggestions.helpers.getRecentAndPopularSearches(4, 4);

    this.renderRecentOrPopularSearches(
      this.recentSearchesEl,
      recentAndPopularSearches.recentSearches,
    );
    this.renderRecentOrPopularSearches(
      this.popularSearchesEl,
      recentAndPopularSearches.popularSearches,
    );
  }

  renderRecentOrPopularSearches(element, recentOrPopularSearches) {
    element
      ?.closest("[aria-hidden]")
      ?.setAttribute("aria-hidden", !recentOrPopularSearches?.length);
    if (!recentOrPopularSearches?.length || !element) return;
    element.innerHTML = this.recentOrPopularItemsHTML(recentOrPopularSearches);
  }

  recentOrPopularItemsHTML(array) {
    return array
      .map((item) => {
        const linkItem = {
          url: `${this.translations.search_url}?${item.queryString}`,
          title: item.displayString,
        };

        return this.linkItemHTML(linkItem);
      })
      .filter(Boolean)
      .join("");
  }

  suggestedItemsHTML(array) {
    return array
      .map((item) => {
        const regEx = new RegExp(this.searchTerm, "ig");
        const suggestedItem = {
          url: item.url,
          title: item.title?.replace(
            regEx,
            `<strong class="hd-Search_Strong">${this.searchTerm}</strong>`,
          ),
        };
        return this.linkItemHTML(suggestedItem);
      })
      .filter(Boolean)
      .join("");
  }

  linkItemHTML(item = { url: "", title: "" }) {
    const { url, title } = item;
    if (!url || !title) return;

    return /* html */ `
      <li class="hd-Search_LinkItem">
        <a class="hd-Search_Link" href="${url}">
          ${title}
        </a>
      </li>
    `;
  }

  updateResultsTitle(count) {
    if (!this.resultsTitleEl) return;

    const productCountString =
      typeof count === "string"
        ? count
        : count > this.maximumResultCount
          ? `( > ${this.maximumResultCount})`
          : `(${count})`;

    this.resultsTitleEl.textContent = translateString(
      this.translations?.quicksearch?.headings?.product_results,
      { count: productCountString },
    );
  }

  updateAllResultsLink(isVisible) {
    if (!this.allResultsLinkEl) return;
    const searchTermQueryString =
      this.searchSuggestions.helpers.getEncodedQueryString({
        query: this.searchTerm,
      });

    const searchUrl = isVisible
      ? `${this.translations.search_url}?${searchTermQueryString}`
      : this.translations.search_url;

    this.allResultsLinkEl.setAttribute("aria-hidden", !isVisible);
    this.allResultsLinkEl.setAttribute("href", searchUrl);
  }

  async productListHTML() {
    if (!this.response?.products?.length) return "";

    const cards = await Promise.all(
      this.response.products.slice(0, 4).map(async (product) => {
        return `
          <li class="hd-Search_Product">
            ${await this.productCardHTML(product)}
          </li>`;
      }),
    );

    return /* html */ `
      <ul class="hd-Search_Products">
        ${cards.join("")}
      </ul>
    `;
  }

  noResultsHTML() {
    return /* html */ `
      <p class="hd-Search_NoResults">
        ${this.translations?.quicksearch?.results?.no_results_found}
      </p>
    `;
  }

  handleInput(event) {
    this.searchTerm = event.target?.value?.toLowerCase() || "";

    const characterCount = this.searchTerm.length;

    if (characterCount < this.minimumCharactersToShowSuggestions) {
      this.setAttribute("state", "default");
    } else {
      this.searchSuggestions.helpers.updateQuery(this.searchTerm);
      this.renderSuggestedResources();
    }
  }

  recordRecentSearch() {
    const searchTermQueryString =
      this.searchSuggestions.helpers.getEncodedQueryString({
        query: this.searchTerm,
      });
    this.searchSuggestions.helpers.recordRecentSearch(searchTermQueryString);
  }

  async productCardHTML(product) {
    return await new ProductCard(product, { lazyload: false }).render();
  }

  get inputDesktopEl() {
    return this.externalEls["input-desktop"]?.element;
  }

  get inputMobileEl() {
    return this.els["input-mobile"]?.element;
  }

  get resultsTitleEl() {
    return this.els["results-title"]?.element;
  }

  get resultsEl() {
    return this.els.results?.element;
  }

  get allResultsLinkEl() {
    return this.els["all-results-link"]?.element;
  }

  get suggestedCollectionsEl() {
    return this.els["suggested-collections"]?.element;
  }

  get suggestedPagesEl() {
    return this.els["suggested-pages"]?.element;
  }

  get recentSearchesEl() {
    return this.els["recent-searches"]?.element;
  }

  get popularSearchesEl() {
    return this.els["popular-searches"]?.element;
  }

  get minimumCharactersToShowSuggestions() {
    return 3;
  }

  get maximumResultCount() {
    return 20;
  }

  get translations() {
    return window.theme.strings;
  }
}

if (!customElements.get("tagalys-quick-search")) {
  customElements.define("tagalys-quick-search", TagalysQuickSearch);
}
